import {
    CardCvcElement,
    CardExpiryElement,
    CardNumberElement,
    useElements,
    useStripe,
} from '@stripe/react-stripe-js'
import {
    StripeCardCvcElementChangeEvent,
    StripeCardExpiryElementChangeEvent,
    StripeCardNumberElementChangeEvent,
} from '@stripe/stripe-js'
import { Field, Input, Label, Message, Toggle } from '@zendeskgarden/react-forms'
import { Validation } from '@zendeskgarden/react-forms/dist/typings/types'
import { useEffect, useState } from 'react'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import LeftArrow from '../../icons/left-arrow.svg?react'
import { RecordFactory } from '../../models/RecordFactory'
import { Source } from '../../models/Source'
import { User } from '../../models/User'
import { isValidPostalCode } from '../../utils'
import { Dots } from '../Dots'
import { NavigationBlockerPrompt } from '../NavigationBlockerPrompt'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import { Spinner } from '../Spinner'
import { PSButton, PSButtonPrimary } from '../app/PSButton'
import { TextFieldV1 } from '../common/fields/TextFieldV1'

type Props = {
    clientID?: string
    onBack?: () => void
}

const viewWillAppear = async (base: PocketComponent<Props>) => {
    return base.props.clientID
        ? await RecordFactory.getInstance().loadUser(base.props.clientID)
        : undefined
}

const render = ({ base, data }: PocketComponentProps<User | undefined, Props>) => {
    const { onBack } = base.props
    const client = data
    const elements = useElements()
    const stripe = useStripe()

    const navigate = usePSNavigate()

    const [stripeReady, setStripeReady] = useState(false)

    const [user, setUser] = useState<User | undefined>(base.me)

    const [name, setName] = useState('')

    const [zipcode, setZipcode] = useState<string | undefined>()
    const [instantPayout, setInstantPayout] = useState(false)

    const [saveEnabled, setSaveEnabled] = useState(false)
    const [hasEdits] = useState(false)

    const [saving, setSaving] = useState(false)

    const [cardValidation, setCardValidation] = useState<Validation | undefined>()
    const [expiryValidation, setExpiryValidation] = useState<Validation | undefined>()
    const [cvcValidation, setCVCValidation] = useState<Validation | undefined>()
    const [zipcodeValidation, setZipcodeValidation] = useState<Validation | undefined>()
    const [nameValidation, setNameValidation] = useState<Validation | undefined>()

    const [numberReady, setNumberReady] = useState(false)
    const [expiryReady, setExpiryReady] = useState(false)
    const [cvcReady, setCVCReady] = useState(false)

    const [cardError, setCardError] = useState('')
    const [expiryError, setExpiryError] = useState('')
    const [cvcError, setCVCError] = useState('')

    const stripeOptions = {
        classes: { base: 'stripeInput' },
        disableLink: true,
        style: {
            base: {
                color: '#2f3941',
                fontWeight: '100',
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '15px',
                '::placeholder': {
                    color: '#999999',
                },
            },
        },
    }

    useEffect(() => {
        setSaveEnabled(
            stripeReady &&
                !zipcodeValidation &&
                !cardValidation &&
                !expiryValidation &&
                !cvcValidation &&
                cvcReady &&
                expiryReady &&
                numberReady &&
                !!name?.length &&
                (zipcode?.length ?? 0) >= 5
        )
    }, [
        stripeReady,
        zipcodeValidation,
        cardValidation,
        expiryValidation,
        cvcValidation,
        numberReady,
        expiryReady,
        cvcReady,
        name,
        zipcode,
    ])

    useEffect(() => {
        setStripeReady(!!stripe && !!elements)
    }, [stripe, elements])

    useEffect(() => {
        if (zipcode) setZipcodeValidation(isValidPostalCode(zipcode ?? '') ? undefined : 'error')
    }, [zipcode, zipcodeValidation])

    useEffect(() => {
        if (nameValidation) setNameValidation(name ? undefined : 'error')
    }, [name, nameValidation])

    const cardOnChangeHandler = (event: StripeCardNumberElementChangeEvent) => {
        setNumberReady(event.complete)
        setCardValidation(event.error ? 'error' : undefined)
        setCardError(event.error?.message ?? '')
    }

    const expiryOnChangeHandler = (event: StripeCardExpiryElementChangeEvent) => {
        setExpiryReady(event.complete)
        setExpiryValidation(event.error ? 'error' : undefined)
        setExpiryError(event.error?.message ?? '')
    }

    const cvcOnChangeHandler = (event: StripeCardCvcElementChangeEvent) => {
        setCVCReady(event.complete)
        setCVCValidation(event.error ? 'error' : undefined)
        setCVCError(event.error?.message ?? '')
    }

    const onSave = async () => {
        if (saving) return

        let validationError = false

        if (!isValidPostalCode(zipcode ?? '')) {
            setZipcodeValidation('error')
            validationError = true
        }

        if (!name) {
            setNameValidation('error')
            validationError = true
        }

        if (!stripe || !elements || validationError) {
            // Stripe.js has not loaded yet. Make sure to disable
            // form submission until Stripe.js has loaded.
            return
        }

        setSaving(true)
        const cardElement = elements!.getElement(CardNumberElement)
        if (cardElement) {
            const { error, token } = await stripe.createToken(cardElement, {
                name: name,
                address_zip: zipcode,
                address_country: 'US',
                currency: 'USD',
            })

            if (error) {
                console.log('[error]', error)
                base.logAndDisplayError(new Error(error.message || 'Error adding card'))
                setSaving(false)
            }
            else if (token) {
                const source: Source = new Source()
                source.token = token.id
                if (client) {
                    source.owner = client
                    source.allowDeposit = false
                    source.cardScanned = false
                    source.allowPayment = false
                    source.type = base.me.sp ? 'debit' : 'credit'
                    source.name = name
                }
                else {
                    source.type = 'card'
                    source.allowDeposit = instantPayout
                }

                const result = await RecordFactory.getInstance().sendSource(source)
                if (!result.ok) {
                    base.logAndDisplayError(result.error)
                    return
                }

                if (onBack) {
                    onBack()
                }
                else {
                    navigate(-1)
                }
            }
            else {
                //Should never happen
            }
        }
    }

    if (!stripe || !elements) {
        return (
            <div className={client ? 'chargeAddCard' : 'servicesSettings'}>
                <div className="header">
                    <div style={{ textAlign: 'center', padding: '128px 0' }}>
                        <Spinner />
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div
            className={
                client ? 'chargeAddCard servicesSettings scheduleClient' : 'servicesSettings'
            }
        >
            {!stripeReady && (
                <div className="loadingOverlay">
                    <div style={{ textAlign: 'center', padding: '128px 0' }}>
                        <Spinner />
                        <h1>Loading...</h1>
                    </div>
                </div>
            )}
            <div className="header">
                {onBack && (
                    <PSButton style={{ marginLeft: 0, marginRight: 16 }} onClick={onBack}>
                        <LeftArrow />
                    </PSButton>
                )}
                <h1>Add a payment card</h1>
                <PSButtonPrimary
                    style={{ height: '40px', marginLeft: 'auto' }}
                    onClick={() => onSave()}
                    disabled={!saveEnabled}
                >
                    {saving && <Dots />}
                    {!saving && 'Save'}
                </PSButtonPrimary>
            </div>
            <div className="separator" />
            <div className="form">
                <Field className="field">
                    <Label className="label">Card #</Label>
                    <CardNumberElement options={stripeOptions} onChange={cardOnChangeHandler} />
                    {!!cardValidation && <Message validation={cardValidation}>{cardError}</Message>}
                </Field>
                <Field className="field">
                    <Label className="label">Expiration date</Label>
                    <CardExpiryElement options={stripeOptions} onChange={expiryOnChangeHandler} />
                    {!!expiryValidation && (
                        <Message validation={expiryValidation}>{expiryError}</Message>
                    )}
                </Field>
                <Field className="field">
                    <Label className="label">CVC</Label>
                    <CardCvcElement options={stripeOptions} onChange={cvcOnChangeHandler} />
                    {!!cvcValidation && <Message validation={cvcValidation}>{cvcError}</Message>}
                </Field>
                <Field className="field">
                    <Label className="label">Name on card</Label>
                    <Input
                        placeholder="John Doe"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                    {!!nameValidation && (
                        <Message validation={nameValidation}>A name is required</Message>
                    )}
                </Field>
                <Field className="field">
                    <Label className="label">Zipcode</Label>
                    <TextFieldV1
                        mask="00000"
                        className="mobileNumberInput"
                        placeholder="90210"
                        validation={zipcodeValidation}
                        onChange={(e) => setZipcode(e.target.value)}
                    />
                    {!!zipcodeValidation && (
                        <Message validation={zipcodeValidation}>
                            That does not appear to be a valid zipcode.
                        </Message>
                    )}
                </Field>
                {!client && (
                    <Field className="field">
                        <Toggle
                            checked={instantPayout}
                            disabled={saving}
                            onChange={(e) => {
                                setInstantPayout(e.target.checked)
                            }}
                        >
                            <Label className="label">Accept instant payouts</Label>
                        </Toggle>
                    </Field>
                )}
                {client && (
                    <p className="clientNote">
                        This card will be securely stored in PocketSuite, allowing you to make
                        authorized charges to this client in the future.
                    </p>
                )}
            </div>
            <NavigationBlockerPrompt when={hasEdits && !saving} />
        </div>
    )
}

/** @deprecated */
export const CardPage = usePSComponent(render, viewWillAppear)
